import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaDownload } from "react-icons/fa";
import API from "../../../lib/apiUtils";
import DownloadPopup from "../download-popup";

const DocumentsSection = () => {


  const itemsPerPage = 4;
  const [currentPage, setCurrentPage] = useState(1);
  const { i18n, t } = useTranslation();
  const [documents, setDocumentsData] = useState([]); // ✅ Set initial value as an empty array
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadPopupOpen, setDownloadPopupOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const openDownloadPopup = (doc) => {
    setSelectedDocument(doc);
    setDownloadPopupOpen(true);
  };



  let language = i18n.language.includes('en') ? 'en' : 'it';

  // API LOADING
  useEffect(() => {
    setLoading(true);
    setDocumentsData([]); // ✅ Ensure documents is always an array
    API.get(`/documents?lang=${language}`)
      .then((response) => {
        setDocumentsData(response || []); // ✅ Ensure response is an array
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching documents:", err);
        setError(err);
        setLoading(false);
      });
  }, [language]);



  if (isLoading) return <p>Loading...</p>;
  if (error) return <p>Error loading documents</p>;
  if (!documents || documents.length === 0) return <p>No documents available</p>;

  const totalPages = Math.ceil(documents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const visibleDocuments = documents.slice(startIndex, startIndex + itemsPerPage);


  return (
    <>
      <DownloadPopup
        isOpen={downloadPopupOpen}
        onClose={() => setDownloadPopupOpen(false)}
        documentName={selectedDocument?.name || ""}
        documentUrl={selectedDocument?.document_file || ""}
      />
      <section className="mx-auto max-w-7xl px-6 py-16 text-center" id="documents">
        {/* Header */}
        <h4 className="text-main-100 uppercase tracking-widest font-normal">{t("sections.downloads")}</h4>
        <h2 className="text-3xl font-bold text-bf-100 mt-2">{t("sections.download_title")}</h2>
        <p className="text-dc-100 mt-4 font-light">{t("sections.download_description")}</p>

        <div className="mt-8 bg-white rounded-lg overflow-hidden">
  {/* Desktop View */}
  <div className="hidden md:block">
    <table className="min-w-full">
      <thead className="border-b border-gray-300 text-gray-700 uppercase text-sm">
        <tr>
          <th className="py-3 px-6 text-left">{t("sections.name")}</th>
          <th className="py-3 px-6">{t("sections.tag")}</th>
          <th className="py-3 px-6 text-center">{t("sections.actions")}</th>
        </tr>
      </thead>
      <tbody>
        {visibleDocuments.map((doc) => (
          <tr key={doc.id} className="border-b hover:bg-gray-50 transition">
            <td className="py-4 px-6 flex items-center space-x-3">
              <img src={doc.image} alt={doc.name} className="w-10 h-10" />
              <div className="flex flex-col w-full">
                <p className="text-gray-900 font-semibold text-left">{doc.name}</p>
                <p className="text-gray-500 text-sm text-left">{doc.size}</p>
              </div>
            </td>
            <td className="py-4 px-6">
              <span className="border-2 border-gray-300 text-gray-500 px-3 py-1 rounded-full text-xs font-normal">
                {doc.document_tags}
              </span>
            </td>
            <td className="py-4 px-6 flex justify-center">
              <button
                onClick={() => openDownloadPopup(doc)}
                className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-color-main text-color-main hover:bg-main-100 hover:text-white transition"
              >
                <FaDownload size={18} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>

  {/* Mobile View */}
  <div className="md:hidden flex flex-col gap-4 p-4">
    {visibleDocuments.map((doc) => (
      <div key={doc.id} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={doc.image} alt={doc.name} className="w-10 h-10" />
          <div className="flex flex-col">
            <p className="font-semibold text-gray-900">{doc.name}</p>
            <p className="text-gray-500 text-sm">{doc.size}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => openDownloadPopup(doc)}
            className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-color-main text-color-main hover:bg-main-100 hover:text-white transition"
          >
            <FaDownload size={18} />
          </button>
        </div>
      </div>
    ))}
  </div>
</div>



        <div className="mt-6 flex justify-center items-center space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            className={`px-3 py-2 rounded-lg ${currentPage === 1 ? "bg-gray-300 cursor-not-allowed text-white" : "bg-gray-200 hover:bg-main-color"
              }`}
            disabled={currentPage === 1}
          >
            &lt;
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-3 py-2 rounded-lg ${currentPage === i + 1 ? "border-2 border-color-main text-color-main" : "bg-gray-200 hover:bg-gray-400"
                }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            className={`px-3 py-2 rounded-lg ${currentPage === totalPages ? "bg-gray-300 cursor-not-allowed text-white" : "border-2 border-gray-200 text-color-main"
              }`}
            disabled={currentPage === totalPages}
          >
            &gt;
          </button>
        </div>

      </section>
    </>

  );
};

export default DocumentsSection;
